import { z } from "zod";
import { dateOnlySchema, emailSchema, listQuerySchema, pageSchema, timestampSchema, uuidSchema } from "./api.ts";
import { timestampsShape } from "./common.ts";
import { contractSummarySchema } from "./contracts.ts";
import { fileSchema } from "./files.ts";
import { invoiceSummarySchema } from "./invoices.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";
import { milestoneSchema, quotaMeterSchema } from "./projects.ts";
import { proposalSummarySchema } from "./proposals.ts";

/**
 * CLIENT PORTAL — the contract shared by the staff API (`/api/v1/portal/*`),
 * the portal API (`/api/portal/v1/*`) and the web app.
 *
 * Two rules shape everything here:
 *  1. A portal identity is NOT a member identity. A person who is both sees two
 *     different worlds and neither inherits the other's rights (PRD US-F1.5).
 *  2. Nothing is client-visible until somebody shares it. `portal_publications`
 *     is the whole allow-list; the absence of a row is a "no" (PRD US-9.4).
 */

// ---------------------------------------------------------------------------
// Roles, capabilities, status
// ---------------------------------------------------------------------------

export const PORTAL_ROLES = ["client_admin", "approver", "viewer", "billing"] as const;
export type PortalRole = (typeof PORTAL_ROLES)[number];
export const portalRoleSchema = z.enum(PORTAL_ROLES);

export const PORTAL_ROLE_LABELS: Record<PortalRole, string> = {
  client_admin: "Client admin",
  approver: "Approver",
  viewer: "Viewer",
  billing: "Billing",
};

/** What a portal identity may do. Deliberately its own vocabulary — never a staff `Permission`. */
export const PORTAL_CAPABILITIES = [
  "projects:view",
  "approvals:act",
  "invoices:view",
  "invoices:pay",
  "proposals:view",
  "proposals:act",
  "contracts:view",
  "contracts:sign",
  "files:view",
  "files:upload",
  "messages:post",
  "colleagues:invite",
] as const;
export type PortalCapability = (typeof PORTAL_CAPABILITIES)[number];
export const portalCapabilitySchema = z.enum(PORTAL_CAPABILITIES);

/** Role defaults. An access row may override them with an explicit list. */
export const PORTAL_ROLE_CAPABILITIES: Record<PortalRole, readonly PortalCapability[]> = {
  client_admin: [...PORTAL_CAPABILITIES],
  approver: ["projects:view", "approvals:act", "files:view", "files:upload", "messages:post", "proposals:view", "contracts:view"],
  viewer: ["projects:view", "files:view"],
  billing: ["invoices:view", "invoices:pay", "files:view", "messages:post", "contracts:view"],
};

/**
 * `requested` is a colleague a client_admin asked for: the row exists, it can be
 * seen and approved by the agency, and it cannot sign in (PRD US-9.1 — "a
 * client_admin can request access for colleagues. The AM approves.").
 */
export const PORTAL_ACCESS_STATUSES = ["requested", "invited", "active", "suspended", "revoked"] as const;
export type PortalAccessStatus = (typeof PORTAL_ACCESS_STATUSES)[number];
export const portalAccessStatusSchema = z.enum(PORTAL_ACCESS_STATUSES);

/** Entity kinds that can be shared with a client. A `null` entityId shares the whole kind. */
export const PORTAL_PUBLISHABLE_TYPES = ["project", "task", "file", "invoice", "proposal", "contract"] as const;
export type PortalPublishableType = (typeof PORTAL_PUBLISHABLE_TYPES)[number];
export const portalPublishableTypeSchema = z.enum(PORTAL_PUBLISHABLE_TYPES);

// ---------------------------------------------------------------------------
// Staff side — portal access (US-9.1)
// ---------------------------------------------------------------------------

/** One person's key to one client's portal. */
export const portalAccessSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  contactId: uuidSchema,
  contactName: z.string(),
  email: z.string(),
  role: portalRoleSchema,
  status: portalAccessStatusSchema,
  capabilities: z.array(portalCapabilitySchema),
  usesRoleDefaults: z.boolean(),
  invitedAt: timestampSchema,
  invitedByUserId: uuidSchema.nullable(),
  activatedAt: timestampSchema.nullable(),
  lastLoginAt: timestampSchema.nullable(),
  lastSeenAt: timestampSchema.nullable(),
  loginCount: z.number().int(),
  notifyByEmail: z.boolean(),
  notifyByWhatsapp: z.boolean(),
  revokedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type PortalAccessDto = z.infer<typeof portalAccessSchema>;

/** POST /api/v1/portal/access (perm `portal:manage`) → 201 PortalAccess. Duplicate contact → CONFLICT. */
export const grantPortalAccessInputSchema = z
  .object({
    clientId: uuidSchema,
    contactId: uuidSchema,
    role: portalRoleSchema,
    email: emailSchema.optional(),
    capabilities: z.array(portalCapabilitySchema).max(PORTAL_CAPABILITIES.length).optional(),
    sendInvitation: z.boolean().optional(),
  })
  .strict();
export type GrantPortalAccessInput = z.input<typeof grantPortalAccessInputSchema>;

/** POST /api/portal/v1/colleagues (capability `colleagues:invite`) → 201 PortalAccess in `requested`. The agency approves it. */
export const requestColleagueInputSchema = z
  .object({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().max(80).nullable().optional(),
    designation: z.string().trim().max(120).nullable().optional(),
    email: emailSchema,
    role: portalRoleSchema,
  })
  .strict();
export type RequestColleagueInput = z.input<typeof requestColleagueInputSchema>;

/** PATCH /api/v1/portal/access/:id (perm `portal:manage`) → PortalAccess. Merge patch; `capabilities: []` restores the role defaults. */
export const updatePortalAccessInputSchema = z
  .object({
    role: portalRoleSchema,
    status: z.enum(["invited", "active", "suspended"]),
    /** Only meaningful while the row is `requested`; approving it sends the invitation. */
    capabilities: z.array(portalCapabilitySchema).max(PORTAL_CAPABILITIES.length).nullable(),
    notifyByEmail: z.boolean(),
    notifyByWhatsapp: z.boolean(),
  })
  .partial()
  .strict();
export type UpdatePortalAccessInput = z.input<typeof updatePortalAccessInputSchema>;

export const listPortalAccessQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  status: portalAccessStatusSchema.optional(),
});
export type ListPortalAccessQuery = z.infer<typeof listPortalAccessQuerySchema>;

// ---------------------------------------------------------------------------
// Staff side — publications (US-9.4)
// ---------------------------------------------------------------------------

/** One "this is shared with you" row. `entityId: null` shares every record of that kind. */
export const portalPublicationSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  entityType: portalPublishableTypeSchema,
  entityId: uuidSchema.nullable(),
  label: z.string().nullable(),
  note: z.string().nullable(),
  publishedByUserId: uuidSchema.nullable(),
  publishedAt: timestampSchema,
  ...timestampsShape,
});
export type PortalPublicationDto = z.infer<typeof portalPublicationSchema>;

/** POST /api/v1/portal/publications (perm `portal:manage`) → 201 PortalPublication. Already shared → 200 with the same row. */
export const publishToPortalInputSchema = z
  .object({
    clientId: uuidSchema,
    entityType: portalPublishableTypeSchema,
    entityId: uuidSchema.nullable().optional(),
    note: z.string().trim().max(500).nullable().optional(),
  })
  .strict();
export type PublishToPortalInput = z.input<typeof publishToPortalInputSchema>;

export const listPortalPublicationsQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema,
  entityType: portalPublishableTypeSchema.optional(),
});
export type ListPortalPublicationsQuery = z.infer<typeof listPortalPublicationsQuerySchema>;

// ---------------------------------------------------------------------------
// Portal authentication (US-F1.5)
// ---------------------------------------------------------------------------

/** POST /api/portal/v1/auth/request-link (public) → 202. Always the same answer, so the endpoint cannot enumerate contacts. */
export const requestPortalLinkInputSchema = z
  .object({
    workspace: z.string().trim().min(1).max(80),
    email: emailSchema,
    channel: z.enum(["email", "otp"]).optional(),
  })
  .strict();
export type RequestPortalLinkInput = z.input<typeof requestPortalLinkInputSchema>;

/** The deliberately uninformative answer to a link/OTP request. */
export const portalChallengeSchema = z.object({
  status: z.literal("sent"),
  channel: z.enum(["email", "otp"]),
  /** Only ever set in development, so the dev portal can be used without a mailbox. */
  devToken: z.string().nullable(),
  expiresInSeconds: z.number().int(),
});
export type PortalChallengeDto = z.infer<typeof portalChallengeSchema>;

/** POST /api/portal/v1/auth/verify (public) → 200 PortalMe + the `l2a_portal` cookie. Used/expired token → 401. */
export const verifyPortalLinkInputSchema = z
  .object({
    token: z.string().trim().min(10).max(200),
    rememberDevice: z.boolean().optional(),
  })
  .strict();
export type VerifyPortalLinkInput = z.input<typeof verifyPortalLinkInputSchema>;

/** POST /api/portal/v1/auth/verify-otp (public) → 200 PortalMe. 5 attempts, then the code is burnt. */
export const verifyPortalOtpInputSchema = z
  .object({
    workspace: z.string().trim().min(1).max(80),
    email: emailSchema,
    code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code"),
    rememberDevice: z.boolean().optional(),
  })
  .strict();
export type VerifyPortalOtpInput = z.input<typeof verifyPortalOtpInputSchema>;

/** The agency's identity as the client sees it. */
export const portalBrandSchema = z.object({
  orgName: z.string(),
  logoUrl: z.string().nullable(),
  brandColor: z.string().nullable(),
  poweredBy: z.boolean(),
});
export type PortalBrandDto = z.infer<typeof portalBrandSchema>;

/** GET /api/portal/v1/me — who the portal thinks you are, and what you may do. */
export const portalIdentitySchema = z.object({
  accessId: uuidSchema,
  contactId: uuidSchema,
  contactName: z.string(),
  email: z.string(),
  clientId: uuidSchema,
  clientName: z.string(),
  role: portalRoleSchema,
  capabilities: z.array(portalCapabilitySchema),
  notifyByEmail: z.boolean(),
  notifyByWhatsapp: z.boolean(),
  lastLoginAt: timestampSchema.nullable(),
  sessionExpiresAt: timestampSchema,
  brand: portalBrandSchema,
});
export type PortalIdentityDto = z.infer<typeof portalIdentitySchema>;

/** PATCH /api/portal/v1/me → PortalMe. The client's own notification preferences. */
export const updatePortalProfileInputSchema = z
  .object({
    notifyByEmail: z.boolean(),
    notifyByWhatsapp: z.boolean(),
  })
  .partial()
  .strict();
export type UpdatePortalProfileInput = z.input<typeof updatePortalProfileInputSchema>;

// ---------------------------------------------------------------------------
// Portal reads
// ---------------------------------------------------------------------------

/** One shared project, as the client sees it (no rates, no internal notes). */
export const portalProjectSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  name: z.string(),
  status: z.string(),
  health: z.string(),
  startDate: dateOnlySchema.nullable(),
  endDate: dateOnlySchema.nullable(),
  percentComplete: z.number().int(),
  openTaskCount: z.number().int(),
  doneTaskCount: z.number().int(),
});
export type PortalProjectDto = z.infer<typeof portalProjectSchema>;

/** A shared task. Only tasks published to this client appear at all. */
export const portalTaskSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  title: z.string(),
  status: z.string(),
  dueDate: dateOnlySchema.nullable(),
  isOverdue: z.boolean(),
  completedAt: timestampSchema.nullable(),
});
export type PortalTaskDto = z.infer<typeof portalTaskSchema>;

export const portalProjectDetailSchema = portalProjectSchema.extend({
  tasks: z.array(portalTaskSchema),
  milestones: z.array(milestoneSchema),
  quotaMeters: z.array(quotaMeterSchema),
});
export type PortalProjectDetailDto = z.infer<typeof portalProjectDetailSchema>;

/** A proof waiting for this client's decision. Filled by the approvals module through the portal's provider seam. */
export const portalApprovalSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  subtitle: z.string().nullable(),
  entityType: z.string(),
  entityId: uuidSchema,
  projectId: uuidSchema.nullable(),
  status: z.string(),
  requestedAt: timestampSchema,
  dueAt: timestampSchema.nullable(),
  previewUrl: z.string().nullable(),
});
export type PortalApprovalDto = z.infer<typeof portalApprovalSchema>;

/** POST /api/portal/v1/approvals/:id/decision (capability `approvals:act`) → PortalApproval. */
export const portalApprovalDecisionInputSchema = z
  .object({
    decision: z.enum(["approved", "changes_requested", "rejected"]),
    comment: z.string().trim().max(4000).nullable().optional(),
  })
  .strict();
export type PortalApprovalDecisionInput = z.input<typeof portalApprovalDecisionInputSchema>;

/** An invoice as the client sees it, with the ways to pay it. */
export const portalInvoiceSchema = z.object({
  invoice: invoiceSummarySchema,
  payUrl: z.string().nullable(),
  pdfUrl: z.string().nullable(),
  upiUri: z.string().nullable(),
  lastViewedAt: timestampSchema.nullable(),
});
export type PortalInvoiceDto = z.infer<typeof portalInvoiceSchema>;

export const portalStatementLineSchema = z.object({
  date: dateOnlySchema,
  kind: z.enum(["invoice", "credit_note", "payment"]),
  reference: z.string(),
  description: z.string(),
  debitMinor: minorSchema,
  creditMinor: minorSchema,
  currency: currencyCodeSchema,
});
export type PortalStatementLineDto = z.infer<typeof portalStatementLineSchema>;

/** GET /api/portal/v1/statement (capability `invoices:view`) — documents, receipts and the running balance. */
export const portalStatementSchema = z.object({
  clientId: uuidSchema,
  clientName: z.string(),
  currency: currencyCodeSchema,
  openingBalanceMinor: minorSchema,
  outstandingMinor: minorSchema,
  overdueMinor: minorSchema,
  creditMinor: minorSchema,
  lines: z.array(portalStatementLineSchema),
});
export type PortalStatementDto = z.infer<typeof portalStatementSchema>;

/** GET /api/portal/v1/dashboard — actions first, then the work (US-9.3). */
export const portalDashboardSchema = z.object({
  clientId: uuidSchema,
  clientName: z.string(),
  brand: portalBrandSchema,
  pendingApprovals: z.array(portalApprovalSchema),
  unpaidInvoices: z.array(portalInvoiceSchema),
  contractsToSign: z.array(contractSummarySchema),
  proposalsToReview: z.array(proposalSummarySchema),
  activeProjects: z.array(portalProjectSchema),
  recentFiles: z.array(fileSchema),
  unreadMessageCount: z.number().int(),
  outstandingMinor: minorSchema,
  overdueMinor: minorSchema,
  currency: currencyCodeSchema,
});
export type PortalDashboardDto = z.infer<typeof portalDashboardSchema>;

/** A proposal the client may open, with the signing link when one exists. */
export const portalProposalSchema = z.object({
  proposal: proposalSummarySchema,
  reviewUrl: z.string().nullable(),
});
export type PortalProposalDto = z.infer<typeof portalProposalSchema>;

/** A contract the client may open, with the e-sign link when one exists. */
export const portalContractSchema = z.object({
  contract: contractSummarySchema,
  signUrl: z.string().nullable(),
});
export type PortalContractDto = z.infer<typeof portalContractSchema>;

// ---------------------------------------------------------------------------
// Messages (US-9.8) — the CLIENT thread, never the internal one
// ---------------------------------------------------------------------------

export const portalMessageSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  projectId: uuidSchema.nullable(),
  authorType: z.enum(["client", "staff"]),
  authorName: z.string(),
  authorContactId: uuidSchema.nullable(),
  authorUserId: uuidSchema.nullable(),
  body: z.string(),
  attachments: z.array(fileSchema),
  createdAt: timestampSchema,
});
export type PortalMessageDto = z.infer<typeof portalMessageSchema>;

/** POST /api/portal/v1/messages (capability `messages:post`) / POST /api/v1/portal/messages (perm `messages:write`). */
export const postPortalMessageInputSchema = z
  .object({
    body: z.string().trim().min(1).max(8000),
    projectId: uuidSchema.nullable().optional(),
    attachmentFileIds: z.array(uuidSchema).max(10).optional(),
  })
  .strict();
export type PostPortalMessageInput = z.input<typeof postPortalMessageInputSchema>;

/** The staff form adds the client the message belongs to. */
export const postStaffPortalMessageInputSchema = postPortalMessageInputSchema.extend({ clientId: uuidSchema }).strict();
export type PostStaffPortalMessageInput = z.input<typeof postStaffPortalMessageInputSchema>;

export const listPortalMessagesQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
});
export type ListPortalMessagesQuery = z.infer<typeof listPortalMessagesQuerySchema>;

// ---------------------------------------------------------------------------
// Portal activity (US-9.4 acceptance criteria — "Kavita viewed INV/26-27/0142")
// ---------------------------------------------------------------------------

export const portalViewSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  contactId: uuidSchema.nullable(),
  contactName: z.string(),
  entityType: z.string(),
  entityId: uuidSchema.nullable(),
  path: z.string().nullable(),
  ip: z.string().nullable(),
  viewedAt: timestampSchema,
});
export type PortalViewDto = z.infer<typeof portalViewSchema>;

export const listPortalActivityQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  entityType: z.string().max(40).optional(),
  entityId: uuidSchema.optional(),
});
export type ListPortalActivityQuery = z.infer<typeof listPortalActivityQuerySchema>;

// ---------------------------------------------------------------------------
// Page helpers the routes use directly
// ---------------------------------------------------------------------------

export const portalAccessPageSchema = pageSchema(portalAccessSchema);
export const portalPublicationPageSchema = pageSchema(portalPublicationSchema);
export const portalMessagePageSchema = pageSchema(portalMessageSchema);
export const portalViewPageSchema = pageSchema(portalViewSchema);

/** Resolves the capabilities of one access row: an explicit list wins, otherwise the role defaults. */
export function portalCapabilitiesFor(role: PortalRole, override?: readonly string[] | null): PortalCapability[] {
  if (override && override.length) {
    const allowed = new Set<string>(PORTAL_CAPABILITIES);
    return override.filter((c): c is PortalCapability => allowed.has(c));
  }
  return [...PORTAL_ROLE_CAPABILITIES[role]];
}
